import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, LogOut, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const VIS_OPTS = [
  { v: "institution", label: "Everyone at institution" },
  { v: "buddies", label: "Buddies only" },
  { v: "private", label: "Private" },
];

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user !== false)
      setForm({ name: user.name, bio: user.bio || "", phone: user.phone || "",
                privacy_profile: user.privacy_profile || "institution",
                privacy_activity: user.privacy_activity || "buddies" });
  }, [user]);

  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/users/me", form);
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="profile-page">
      <div className="t-card p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-secondary/30 flex items-center justify-center font-display font-black text-2xl">
          {user.name[0]}
        </div>
        <div>
          <h1 className="font-display font-black text-2xl flex items-center gap-2">
            {user.name}
            {user.verification_status === "verified" && <BadgeCheck className="w-5 h-5 text-primary" data-testid="profile-verified-badge" />}
          </h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          <p className="text-xs text-muted-foreground mt-1">{user.completed_rides || 0} rides completed · role: {user.role.replace("_", " ")}</p>
        </div>
      </div>

      <form onSubmit={save} className="t-card p-6 space-y-4">
        <h2 className="font-display font-bold">Edit profile</h2>
        <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="rounded-2xl" />
        <Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="rounded-2xl" />
        <Textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio — tell your campus who you are" className="rounded-2xl" />

        <div className="pt-2">
          <h3 className="font-display font-bold text-sm flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-primary" /> Privacy</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Profile visibility</span>
              <Select value={form.privacy_profile} onValueChange={(v) => setForm({ ...form, privacy_profile: v })}>
                <SelectTrigger data-testid="privacy-profile-select" className="rounded-2xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{VIS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Activity visibility</span>
              <Select value={form.privacy_activity} onValueChange={(v) => setForm({ ...form, privacy_activity: v })}>
                <SelectTrigger data-testid="privacy-activity-select" className="rounded-2xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{VIS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <Button data-testid="profile-save-btn" disabled={busy} type="submit" className="w-full rounded-full font-bold">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </form>

      <button data-testid="profile-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
        className="w-full t-card p-4 flex items-center justify-center gap-2 text-destructive font-bold text-sm hover:-translate-y-0.5 transition-transform duration-200">
        <LogOut className="w-4 h-4" /> Log out
      </button>
    </div>
  );
}
